import React, { useState } from 'react';
import { Table, Column } from '../../components/ui/Table';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Search, Download, X } from 'lucide-react';
export interface OnboardingApplication {
  id: string;
  merchantId: string;
  merchantName: string;
  merchantType: 'New' | 'Partner';
  partnerType: string;
  status: 'Pending' | 'Submitted to Checker' | 'Discrepant' | 'Reverted to Maker' | 'Approved' | 'Rejected';
  mechanism: 'STP' | 'Validation';
  mechanismStage: 'Completed' | 'Maker' | 'Checker' | 'Approver';
  date: string;
  submittedBy: string;
}
// Mock Data Generator
const generateMockOnboardingApplications = (): OnboardingApplication[] => {
  const statuses = ['Pending', 'Submitted to Checker', 'Discrepant', 'Reverted to Maker', 'Approved', 'Rejected'] as const;
  const mechanisms = ['STP', 'Validation'] as const;
  const stages = ['Completed', 'Maker', 'Checker', 'Approver'] as const;
  return Array.from({
    length: 15
  }).map((_, i) => {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    return {
      id: `ONB-2024-${String(i + 1).padStart(3, '0')}`,
      merchantId: `MCH-${String(Math.floor(Math.random() * 10000) + 10000)}`,
      merchantName: `Merchant ${String.fromCharCode(65 + i)} Enterprises`,
      merchantType: Math.random() > 0.5 ? 'New' : 'Partner',
      partnerType: Math.random() > 0.5 ? 'Tapsys' : 'Direct',
      status,
      mechanism: mechanisms[Math.floor(Math.random() * mechanisms.length)],
      mechanismStage: stages[Math.floor(Math.random() * stages.length)],
      date: new Date(Date.now() - Math.floor(Math.random() * 10000000000)).toISOString().split('T')[0],
      submittedBy: ['maker@bunyaadfinance.com', 'admin@bunyaadfinance.com'][Math.floor(Math.random() * 2)]
    };
  });
};
export const mockOnboardingApplications = generateMockOnboardingApplications();
interface OnboardingMasterViewProps {
  onNavigateToDetail: (appId: string) => void;
  applicationStatuses?: Record<string, {
    status: string;
    logs: Array<any>;
  }>;
}
export function OnboardingMasterView({
  onNavigateToDetail,
  applicationStatuses = {}
}: OnboardingMasterViewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [merchantTypeFilter, setMerchantTypeFilter] = useState('');
  const getStatusBadgeColor = (status: string) => {
    const statusMap: Record<string, {
      bg: string;
      text: string;
    }> = {
      Pending: {
        bg: 'bg-blue-100',
        text: 'text-blue-800'
      },
      'Submitted to Checker': {
        bg: 'bg-purple-100',
        text: 'text-purple-800'
      },
      Discrepant: {
        bg: 'bg-orange-100',
        text: 'text-orange-800'
      },
      'Reverted to Maker': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800'
      },
      Approved: {
        bg: 'bg-green-100',
        text: 'text-green-800'
      },
      Rejected: {
        bg: 'bg-red-100',
        text: 'text-red-800'
      }
    };
    const style = statusMap[status] || {
      bg: 'bg-gray-100',
      text: 'text-gray-800'
    };
    return `${style.bg} ${style.text}`;
  };
  const data = mockOnboardingApplications.map(app => ({
    ...app,
    status: applicationStatuses[app.id]?.status as any || app.status
  }));
  const filteredApps = data.filter(app => {
    const matchesSearch = app.id.toLowerCase().includes(searchQuery.toLowerCase()) || app.merchantName.toLowerCase().includes(searchQuery.toLowerCase()) || app.merchantId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter ? app.status === statusFilter : true;
    const matchesMerchantType = merchantTypeFilter ? app.merchantType === merchantTypeFilter : true;
    return matchesSearch && matchesStatus && matchesMerchantType;
  });
  const columns: Column<OnboardingApplication>[] = [{
    key: 'id',
    label: 'Application ID'
  }, {
    key: 'merchantId',
    label: 'Merchant ID'
  }, {
    key: 'merchantName',
    label: 'Merchant Name',
    render: item => <span className="font-medium text-gray-900" data-id="element-1864">{item.merchantName}</span>
  }, {
    key: 'merchantType',
    label: 'Merchant Type'
  }, {
    key: 'partnerType',
    label: 'Partner Type'
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(item.status)}`} data-id="element-1865">
          {item.status}
        </span>
  }, {
    key: 'mechanism',
    label: 'Mechanism'
  }, {
    key: 'mechanismStage',
    label: 'Stage',
    render: item => <span className="text-xs" data-id="element-1866">{item.mechanismStage}</span>
  }, {
    key: 'date',
    label: 'Date'
  }, {
    key: 'submittedBy',
    label: 'Submitted By'
  }];
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1867">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1868">
        <div data-id="element-1869">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1870">
            Onboarding Applications
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1871">
            Manage and track merchant onboarding applications.
          </p>
        </div>
        <Button variant="secondary" data-id="element-1872">
          <Download className="mr-2 h-4 w-4" data-id="element-1873" />
          Export Data
        </Button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm space-y-4" data-id="element-1874">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1875">
          <div className="relative col-span-1 md:col-span-2" data-id="element-1876">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-1877" />
            <Input placeholder="Search by App ID, Merchant Name..." className="pl-10" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1878" />
          </div>

          <Select options={[{
          value: 'Pending',
          label: 'Pending'
        }, {
          value: 'Submitted to Checker',
          label: 'Submitted to Checker'
        }, {
          value: 'Discrepant',
          label: 'Discrepant'
        }, {
          value: 'Reverted to Maker',
          label: 'Reverted to Maker'
        }, {
          value: 'Approved',
          label: 'Approved'
        }, {
          value: 'Rejected',
          label: 'Rejected'
        }]} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} placeholder="Filter by Status" data-id="element-1879" />

          <Select options={[{
          value: 'New',
          label: 'New Merchant'
        }, {
          value: 'Partner',
          label: 'Partner Merchant'
        }]} value={merchantTypeFilter} onChange={e => setMerchantTypeFilter(e.target.value)} placeholder="Merchant Type" data-id="element-1880" />
        </div>

        {(searchQuery || statusFilter || merchantTypeFilter) && <div className="flex items-center text-sm text-gray-500" data-id="element-1881">
            <span className="mr-2" data-id="element-1882">Active Filters:</span>
            {searchQuery && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md mr-2 flex items-center" data-id="element-1883">
                Search: {searchQuery}{' '}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSearchQuery('')} data-id="element-1884" />
              </span>}
            {statusFilter && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md mr-2 flex items-center" data-id="element-1885">
                Status: {statusFilter}{' '}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setStatusFilter('')} data-id="element-1886" />
              </span>}
            {merchantTypeFilter && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md mr-2 flex items-center" data-id="element-1887">
                Type: {merchantTypeFilter}{' '}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setMerchantTypeFilter('')} data-id="element-1888" />
              </span>}
            <button className="text-blue-600 hover:text-blue-800 ml-auto" onClick={() => {
          setSearchQuery('');
          setStatusFilter('');
          setMerchantTypeFilter('');
        }} data-id="element-1889">
              Clear All
            </button>
          </div>}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1890">
        <div className="overflow-x-auto" data-id="element-1891">
          <Table columns={columns} data={filteredApps} onRowClick={app => onNavigateToDetail(app.id)} keyExtractor={item => item.id} data-id="element-1892" />
        </div>
      </div>
    </div>;
}